import { useEffect, useRef } from 'react';
import { useAIChat } from '../contexts/AIChatContext';
import { ChatAttachment } from '../services/aiChat';

interface Options {
  /** Panel is open, or full-page view is mounted */
  active: boolean;
  buildContext: () => ReturnType<typeof import('../services/aiChat').buildCRMContext>;
  sendMessage: (text: string, crmContext: any, attachments?: ChatAttachment[]) => Promise<void>;
  createConversation: () => void;
  setInput: (value: string) => void;
  focusInput?: () => void;
  isLoading: boolean;
}

/** Applies a pending contextual chat request (prefill / auto-send) once the chat UI is ready. */
export function usePendingChatBootstrap({
  active,
  buildContext,
  sendMessage,
  createConversation,
  setInput,
  focusInput,
  isLoading,
}: Options) {
  const { pendingChatRequest, consumePendingChatRequest } = useAIChat();
  const handledRef = useRef<string | null>(null);

  useEffect(() => {
    if (!active || !pendingChatRequest || isLoading) return;

    const key = JSON.stringify(pendingChatRequest);
    if (handledRef.current === key) return;

    const req = consumePendingChatRequest();
    if (!req) return;
    handledRef.current = key;

    if (req.newConversation) {
      createConversation();
    }

    if (req.prompt) {
      setInput(req.prompt);
      if (req.autoSend) {
        const timer = window.setTimeout(() => {
          void sendMessage(req.prompt!, buildContext());
        }, 200);
        return () => window.clearTimeout(timer);
      }
      focusInput?.();
    }
  }, [
    active,
    pendingChatRequest,
    consumePendingChatRequest,
    createConversation,
    setInput,
    focusInput,
    sendMessage,
    buildContext,
    isLoading,
  ]);
}
